/* eslint-disable @typescript-eslint/indent */
import React, { Dispatch, SetStateAction, useRef, useMemo } from 'react';
import Image from 'next/image';
import ImageLoaded from '@components/common/ImageLoaded/ImageLoaded';
import Product from '@customTypes/product';
import { CartItemDetails } from '@customTypes/cart-items';
import HotelDetails from '@customTypes/hotel-details';
import { timeSlotProductSelected } from '@events/timeSlottedProduct';
import useAmplitudePayloads from '@hooks/useAmplitudePayloads';
import { useEvents } from '@events/EventsProvider';
import { SELECT_PRODUCT_CARD } from '@constants/amplitudeEvents';
import { productClicked } from '@events/DLP';
import useHotSpot from '@hooks/useHotSpot';
import iconPlus from '@assets/images/icon-plus.svg';
import { getPhotoUrlFromProduct, isProductUnAvailable } from '@helpers/product';
import { getProductPriceForGuestType } from '@utils/fairPricing';
import { FAIR_PRICING_VARIANT } from '@constants/fairPricing';
import { getCdnImageUrl } from '@utils/cloudflare';
import ProductDetailsPriceWithSTP from './ProductDetailsPriceWithSTP';
import ProductTagFactoryWithSTP from './ProductTagFactoryWithSTP';

type ProductComponentWithImageProps = {
  products: Product[] | undefined;
  product: Product;
  date: Date | undefined;
  cartData: CartItemDetails | undefined;
  hotelDetails: HotelDetails;
  showFairPricing: boolean;
  setIsBottomDrawerOpen: Dispatch<SetStateAction<boolean>>;
};

export default function ProductComponentWithSTP({
  products,
  product,
  date,
  cartData,
  hotelDetails,
  showFairPricing,
  setIsBottomDrawerOpen,
}: ProductComponentWithImageProps) {
  const divRef = useRef<HTMLDivElement>(null);
  const { getTheTimeWithTheLowestPrice } = useHotSpot();
  const { track } = useEvents();
  const { generateHotelPayload, generateProductPayload } = useAmplitudePayloads();

  const photoUrl = useMemo(() => getPhotoUrlFromProduct(product)?.url, [product]);

  const hasProductPhotoUrl = useMemo(() => {
    if (!photoUrl || photoUrl.includes('/null')) {
      return false;
    }

    return true;
  }, [photoUrl]);

  const onCardClick = () => {
    setIsBottomDrawerOpen(true);

    // Event
    if (product.is_product_time_group && product.children) {
      timeSlotProductSelected(product);
    }

    const hotelPropertyGroup = generateHotelPayload(hotelDetails, products);
    const productPropertyGroup = generateProductPayload(product);

    track(SELECT_PRODUCT_CARD, {
      ...hotelPropertyGroup,
      products: [productPropertyGroup],
      is_photo: hasProductPhotoUrl,
    });

    productClicked(product, hotelDetails);
  };

  return (
    <button
      type="button"
      onClick={onCardClick}
      className="border border-rp-gray-divider cursor-pointer flex items-center justify-between outline-none pl-4 relative rounded-xl py-18.5px w-full h-24"
    >
      <div className="flex flex-col w-full gap-1.5 items-baseline">
        <div className="flex items-center space-x-11px">
          <div className="flex flex-col items-start">
            <p
              className={`font-rp-pn-semi-bold text-left text-sm -tracking-0.14 line-clamp-2 pr-4 ${
                isProductUnAvailable(product, cartData, hotelDetails, date)
                  ? 'text-rp-text-disabled'
                  : 'text-custom-black'
              }`}
            >
              {product.name}
            </p>
          </div>
        </div>

        <div>
          {!date || product.is_product_time_group ? (
            <p
              className={`flex-shrink-0 font-medium font-rp-pn-regular leading-10px text-left text-10 mb-0.5 ${
                isProductUnAvailable(product, cartData, hotelDetails, date)
                  ? 'text-rp-text-disabled'
                  : 'text-rp-disabled'
              }`}
            >
              Starting at
            </p>
          ) : null}

          <div ref={divRef} className="flex items-center space-x-2 text-sm">
            <ProductDetailsPriceWithSTP
              cartData={cartData}
              date={date}
              hotelDetails={hotelDetails}
              personType="adult"
              product={
                product && product.is_product_time_group && product.children
                  ? getTheTimeWithTheLowestPrice(product.children, hotelDetails, cartData)
                  : product
              }
              showFairPricing={showFairPricing}
            />

            <ProductTagFactoryWithSTP
              date={date}
              cartData={cartData}
              hotelDetails={hotelDetails}
              product={
                product && product.is_product_time_group && product.children
                  ? getTheTimeWithTheLowestPrice(product.children, hotelDetails, cartData)
                  : product
              }
            />
          </div>

          {/* Fair Pricing */}
          {!(date && isProductUnAvailable(product, cartData, hotelDetails, date)) &&
          showFairPricing ? (
            <p
              className={`flex-shrink-0 font-medium font-rp-pn-regular leading-10px text-left text-10 text-rp-disabled mt-0.5 ${
                isProductUnAvailable(product, cartData, hotelDetails, date)
                  ? 'text-rp-text-disabled'
                  : 'text-rp-disabled'
              }`}
            >
              {FAIR_PRICING_VARIANT === 'variant1'
                ? 'Includes fees'
                : `+$${(+getProductPriceForGuestType(product, 'adult', showFairPricing)).toFixed(
                    0,
                  )} in fees`}
            </p>
          ) : null}
        </div>
      </div>

      <div className="w-112 h-24 max-w-112 relative flex flex-shrink-0">
        {hasProductPhotoUrl && (
          <ImageLoaded
            url={[photoUrl, getCdnImageUrl(112, photoUrl, '', 100)]}
            layout="fill"
            objectFit="cover"
            classes="rounded-tr-lg rounded-br-lg"
            priority
            alt={product.name}
          />
        )}

        <div className="absolute z-10 right-2 bottom-0">
          <Image src={iconPlus} alt="add-icon" />
        </div>
      </div>
    </button>
  );
}
